import { useEffect, useRef, useState } from 'react'
import styles from './picker.module.css'

/**
 * Pick one existing thing from a list, by typing part of its name.
 *
 * One component for both sides of a note↔task link (ADR 0012). It started as two near-identical
 * copies, one per feature folder, and the copies immediately proved the point: the same layout bug
 * shipped twice, and each would have needed its own fix.
 *
 * Two things it must get right, both learned the hard way:
 *
 *  1. **Rows do not shrink.** The list is a column flex box with a `max-height`, so without
 *     `flex: none` on each row (in the stylesheet) flex squeezes fifty rows into the visible height
 *     and every one of them collapses into an unreadable sliver. `ui-spec.md` §5.2 says this out
 *     loud about board cards, for exactly the same reason.
 *  2. **There is always a way out.** Opening a picker and deciding not to pick anything is a normal
 *     thing to do, so it closes on Escape, on a click anywhere outside it, and on a named button —
 *     never only by choosing something and undoing it afterwards.
 */
export function Picker<T extends { id: number }>({
  label,
  placeholder,
  options,
  match,
  renderOption,
  emptyText,
  onPick,
  onClose,
}: {
  /** Accessible name of the search field. */
  label: string
  placeholder: string
  /** Everything that could be picked; filtering happens here, on what the caller passed in. */
  options: T[]
  match: (option: T, needle: string) => boolean
  renderOption: (option: T) => React.ReactNode
  /** `hasQuery` separates "nothing matches what you typed" from "there is nothing to pick". */
  emptyText: (hasQuery: boolean) => string
  onPick: (option: T) => void
  onClose: () => void
}): React.JSX.Element {
  const [query, setQuery] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)

  /**
   * Closing on an outside click listens for `pointerdown`, not `click`: a click fires after the
   * mouse comes back up, which is late enough that a person dragging the list scrollbar off the
   * edge of the picker would close the thing they are scrolling.
   */
  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const box = boxRef.current
      if (box && event.target instanceof Node && !box.contains(event.target)) onClose()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [onClose])

  const needle = query.trim().toLowerCase()
  const matches = needle ? options.filter((option) => match(option, needle)) : options

  return (
    <div className={styles.box} ref={boxRef}>
      <div className={styles.bar}>
        <input
          className={styles.input}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- pole pojawia się na życzenie, po kliknięciu
          autoFocus
          value={query}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            // Escape zamyka to, co jest na wierzchu, i tylko to. Bez zatrzymania zdarzenia
            // globalny skrót z useHotkeys zamykał przy okazji cały drawer zadania — czyli
            // rezygnacja z wyboru notatki kosztowała utratę otwartego zadania.
            e.stopPropagation()
            onClose()
          }}
        />
        <button type="button" className={styles.cancel} onClick={onClose}>
          Anuluj
        </button>
      </div>

      <div className={styles.list}>
        {matches.map((option) => (
          <button
            key={option.id}
            type="button"
            className={styles.option}
            onClick={() => onPick(option)}
          >
            {renderOption(option)}
          </button>
        ))}

        {matches.length === 0 && <p className={styles.empty}>{emptyText(needle.length > 0)}</p>}
      </div>

      {/* Licznik, nie „pokazuję 8 z 61": lista przewija się w całości, więc chodzi tylko o to, żeby
          było widać, ile jeszcze jest pod spodem. */}
      {matches.length > VISIBLE_ROWS && (
        <p className={styles.count}>{matches.length} pozycji — przewiń albo zawęź</p>
      )}
    </div>
  )
}

/** Ile wierszy widać bez przewijania — zgodnie z `max-height` listy w picker.module.css. */
const VISIBLE_ROWS = 7
